import type { ReactNode } from "react";

export interface PageProps {
	/** Page title (rendered as an h1). */
	title?: ReactNode;
	/** Sub-line under the title. */
	description?: ReactNode;
	/** Top-right actions (e.g. a primary Button). */
	actions?: ReactNode;
	/**
	 * Constrain content to a comfortable reading width. Default false (full width —
	 * right for tables/dashboards). Set true for forms/detail pages.
	 */
	narrow?: boolean;
	className?: string;
	children: ReactNode;
}

/**
 * The single page-content layer for every operator screen. Gives ONE consistent
 * padding scale (responsive) + the standard title/description/actions header, so
 * spacing is identical across all apps instead of each page hand-rolling its own
 * `p-8`/`p-6`. Pair with `Table` for data screens. Change the padding here → it
 * changes platform-wide.
 *
 *   <Page title="Global Entities" description="…" actions={<Button>Create</Button>}>
 *     <Table … />
 *   </Page>
 */
export function Page({
	title,
	description,
	actions,
	narrow = false,
	className = "",
	children,
}: PageProps) {
	const hasHeader = title != null || description != null || actions != null;
	return (
		<div className={`p-4 sm:p-6 lg:p-8 ${className}`.trim()}>
			<div className={narrow ? "mx-auto w-full max-w-3xl" : "w-full"}>
				{hasHeader && (
					<div className="mb-6 flex flex-wrap items-start justify-between gap-3">
						<div className="min-w-0">
							{title != null && <h1 className="text-2xl font-bold text-content">{title}</h1>}
							{description != null && <p className="mt-1 text-sm text-muted">{description}</p>}
						</div>
						{actions != null && (
							<div className="flex flex-shrink-0 items-center gap-2">{actions}</div>
						)}
					</div>
				)}
				{children}
			</div>
		</div>
	);
}
